import { useState } from "react";
import {
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Flag,
  RefreshCcw,
  Zap,
} from "lucide-react";
import { motion } from "framer-motion";
import { useStratagems } from "@/context/StratagemContext";
import ArrowDisplay from "@/components/ArrowDisplay";
import usePracticeSession from "@/components/practice/usePracticeSession";
import MetricCard from "@/components/practice/MetricCard";
import StratagemSelector from "@/components/practice/StratagemSelector";
import TouchDPad from "@/components/practice/TouchDPad";
import RapidFire from "@/components/practice/RapidFire";
import { formatMs, formatPercent } from "@/utils/format";
import { fadeInUp, transition, transitionDelayed } from "@/utils/motion";
import { play as sfx, playHover } from "@/utils/sounds";

const directionIcon = {
  up: ArrowUp,
  down: ArrowDown,
  left: ArrowLeft,
  right: ArrowRight,
};

const Practice = () => {
  const { stratagems, loading, error } = useStratagems();
  const session = usePracticeSession(stratagems);
  const [rapidFire, setRapidFire] = useState(false);

  const handleSelect = (id) => {
    session.setSelectedId(id);
    session.resetSession();
  };

  if (rapidFire) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 md:px-12" data-testid="practice-page">
        <RapidFire
          stratagems={stratagems}
          onExit={() => setRapidFire(false)}
        />
      </div>
    );
  }

  return (
    <div
      className="max-w-7xl mx-auto px-6 py-12 md:px-12"
      data-testid="practice-page"
    >
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <motion.section
          className="lg:col-span-4 space-y-6"
          {...fadeInUp}
          transition={transition}
        >
          <StratagemSelector
            search={session.search}
            setSearch={session.setSearch}
            category={session.category}
            setCategory={session.setCategory}
            categories={session.categories}
            newCount={session.newCount}
            filtered={session.filtered}
            selectedId={session.selectedId}
            onSelect={handleSelect}
            loading={loading}
            error={error}
            gamepadConnected={session.gamepadConnected}
            isTouchDevice={session.isTouchDevice}
          />
        </motion.section>

        <motion.section
          className="lg:col-span-8 space-y-6"
          {...fadeInUp}
          transition={transitionDelayed}
        >
          {/* Rapid Fire — prominent entry */}
          <button
            onClick={() => { sfx("round_start"); setRapidFire(true); }}
            onMouseEnter={playHover}
            disabled={loading || stratagems.length === 0}
            className="w-full hud-card clip-corners p-5 flex items-center gap-5 border-2 border-[#FFE800]/50 bg-[#FFE800]/[0.04] hover:bg-[#FFE800]/10 hover:border-[#FFE800] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed group"
            data-testid="rapid-fire-button"
          >
            <div className="flex items-center justify-center h-12 w-12 border border-[#FFE800]/60 bg-[#FFE800]/10 group-hover:bg-[#FFE800]/20 transition-colors duration-200">
              <Zap className="h-6 w-6 text-[#FFE800]" />
            </div>
            <div className="text-left">
              <span className="font-headings text-xl uppercase tracking-widest text-[#FFE800]">Rapid Fire</span>
              <p className="text-xs text-gray-400 mt-0.5 tracking-normal normal-case font-data">Random stratagems, back to back. Compete for the fastest SPM.</p>
            </div>
          </button>

          <div className="hud-card clip-corners p-6" data-testid="practice-session">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p
                  className="text-xs uppercase tracking-[0.3em] text-gray-500"
                  data-testid="selected-stratagem-category"
                >
                  {session.selected
                    ? session.selected.category
                    : "No stratagem selected"}
                </p>
                <h2
                  className="mt-2 font-headings text-3xl uppercase tracking-widest text-white"
                  data-testid="selected-stratagem-name"
                >
                  {session.selected
                    ? session.selected.name
                    : "Choose a stratagem"}
                </h2>
              </div>
              <button
                onClick={session.resetSession}
                onMouseEnter={playHover}
                className="flex items-center gap-2 border border-white/20 px-4 py-2 text-xs font-data text-white hover:border-[#FFE800]"
                data-testid="reset-session-button"
              >
                <RefreshCcw className="h-4 w-4" />
                Reset Session
              </button>
            </div>
            <div className="mt-6">
              {session.selected ? (
                <ArrowDisplay
                  sequence={session.selected.code}
                  progress={session.inputSequence.length}
                  testIdPrefix="practice-target"
                />
              ) : (
                <p
                  className="text-sm text-gray-400"
                  data-testid="practice-empty-state"
                >
                  Select a stratagem on the left to begin drilling.
                </p>
              )}
            </div>

            {session.selected && (
              <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_0.7fr]">
                <div>
                  <p
                    className={`font-data text-sm uppercase tracking-[0.3em] ${session.statusColor}`}
                    data-testid="practice-status-text"
                  >
                    {session.statusText}
                  </p>
                  <div
                    className={`mt-3 border border-white/10 px-3 py-2 text-xs font-data ${
                      session.isLocked ? "text-red-300" : "text-gray-400"
                    }`}
                    data-testid="practice-status-detail"
                  >
                    {session.statusDetail}
                  </div>
                  <p
                    className="mt-2 text-xs text-gray-500"
                    data-testid="practice-timer"
                  >
                    Active timer: {formatMs(session.elapsed)}
                  </p>
                  <div
                    className="mt-4 flex flex-wrap items-center gap-3"
                    data-testid="practice-input-buffer"
                  >
                    {session.inputSequence.length === 0 ? (
                      <span
                        className="text-xs text-gray-500"
                        data-testid="practice-input-empty"
                      >
                        Awaiting input...
                      </span>
                    ) : (
                      session.inputSequence.map((dir, i) => {
                        const Icon = directionIcon[dir];
                        return (
                          <span
                            key={`${dir}-${i}`}
                            className="flex items-center justify-center rounded-sm border border-white/10 bg-black/50 p-2 text-[#FFE800]"
                            data-testid={`practice-input-${i}`}
                          >
                            <Icon className="h-5 w-5" />
                          </span>
                        );
                      })
                    )}
                  </div>
                  {session.isTouchDevice && (
                    <div
                      className="mt-6 min-h-[140px]"
                      data-testid="practice-dpad-container"
                    >
                      <TouchDPad
                        onInput={session.handleInput}
                        isLocked={session.isLocked}
                      />
                    </div>
                  )}
                  <div
                    className="mt-4 flex items-center gap-3 text-xs text-gray-500"
                    data-testid="practice-last-result"
                  >
                    <Flag className="h-4 w-4 text-[#FFE800]" />
                    Last attempt:{" "}
                    {session.lastResult
                      ? session.lastResult === "success"
                        ? formatMs(session.lastTimeMs)
                        : "Failed"
                      : "--"}
                  </div>
                </div>
                <div className="grid gap-4">
                  <MetricCard
                    label="Session accuracy"
                    value={formatPercent(session.sessionAccuracy)}
                    helper={`${session.successesSession}/${session.attemptsSession} successes`}
                    testId="session-accuracy"
                  />
                  <MetricCard
                    label="Inputs / min"
                    value={session.sessionInputsPerMin || 0}
                    helper="Inputs per minute on successful runs"
                    testId="session-ipm"
                  />
                  <MetricCard
                    label="Avg time"
                    value={formatMs(session.sessionAvg)}
                    helper="Successful average"
                    testId="session-avg-time"
                  />
                  <MetricCard
                    label="Current streak"
                    value={session.currentStreak}
                    helper="Perfect combos in a row"
                    testId="session-streak"
                  />
                </div>
              </div>
            )}
          </div>

          <div
            className="grid gap-4 md:grid-cols-3"
            data-testid="practice-lifetime"
          >
            <MetricCard
              label="Lifetime accuracy"
              value={formatPercent(session.lifetimeAccuracy)}
              helper={
                session.selected ? "Across all attempts" : "Select a stratagem"
              }
              testId="lifetime-accuracy"
            />
            <MetricCard
              label="Lifetime avg"
              value={formatMs(session.lifetimeAvg)}
              helper={session.selected ? "All successes" : "--"}
              testId="lifetime-avg"
            />
            <MetricCard
              label="Best time"
              value={formatMs(session.lifetimeBestTime)}
              helper={session.selected ? "Personal record" : "--"}
              testId="lifetime-best"
            />
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Practice;
